import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle, AlertTriangle, XCircle, RefreshCw, ArrowLeft, ShieldCheck,
  FileText, Users, Store, ShoppingBag, Info
} from 'lucide-react';
import { api } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '../components/ui/card';

// ─── helpers ────────────────────────────────────────────────────────────────

const LEVEL_CONFIG = {
  OK:      { icon: CheckCircle,   color: 'text-green-600',  bg: 'bg-green-50',  border: 'border-green-200', badge: 'bg-green-100 text-green-800' },
  WARNING: { icon: AlertTriangle, color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200', badge: 'bg-yellow-100 text-yellow-800' },
  ERROR:   { icon: XCircle,       color: 'text-red-600',    bg: 'bg-red-50',    border: 'border-red-200',    badge: 'bg-red-100 text-red-800'     },
};

function StatusBadge({ status }) {
  const cfg = LEVEL_CONFIG[status] || LEVEL_CONFIG.OK;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.badge}`}>
      <cfg.icon className="w-3 h-3" />
      {status}
    </span>
  );
}

function IssueRow({ issue }) {
  const cfg = LEVEL_CONFIG[issue.level] || LEVEL_CONFIG.OK;
  const Icon = cfg.icon;
  const hasDetail = issue.detail && Object.keys(issue.detail).length > 0;

  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg border ${cfg.bg} ${cfg.border}`}>
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${cfg.color}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-800">{issue.message}</p>
        {hasDetail && (
          <p className="mt-1 text-xs text-gray-500 font-mono break-all">
            {Object.entries(issue.detail).map(([k, v]) => `${k}=${v}`).join('  ·  ')}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── main component ──────────────────────────────────────────────────────────

const AdminLegalAuditPage = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const runAudit = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/legal/admin/audit');
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Errore durante l\'esecuzione dell\'audit');
    } finally {
      setLoading(false);
    }
  }, []);

  // Raggruppa issue per numero di check
  const grouped = data
    ? data.issues.reduce((acc, issue) => {
        const key = issue.check;
        if (!acc[key]) acc[key] = { name: issue.check_name, items: [] };
        acc[key].items.push(issue);
        return acc;
      }, {})
    : {};

  const statsItems = data ? [
    { icon: FileText,   label: 'Documenti totali',   value: data.stats.legal_documents_total },
    { icon: FileText,   label: 'Documenti attivi',   value: data.stats.legal_documents_active },
    { icon: Users,      label: 'Accettazioni utenti', value: data.stats.user_acceptances_total },
    { icon: Store,      label: 'Accettazioni vendor', value: data.stats.vendor_acceptances_total },
    { icon: ShoppingBag,label: 'Accettazioni ordini', value: data.stats.order_acceptances_total },
    { icon: Users,      label: 'Utenti distinti',    value: data.stats.user_acceptances_distinct_users },
  ] : [];

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">

      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to="/admin/legal" className="text-gray-500 hover:text-gray-700 transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-blue-600" />
            Audit Sistema Legale
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Verifica coerenza e integrità delle accettazioni documenti legali. Solo lettura.
          </p>
        </div>
        <Button onClick={runAudit} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Esecuzione…' : data ? 'Riesegui audit' : 'Esegui audit'}
        </Button>
      </div>

      {/* Errore */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <XCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Stato iniziale */}
      {!data && !loading && !error && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 gap-4 text-center">
            <ShieldCheck className="w-12 h-12 text-gray-300" />
            <div>
              <p className="font-medium text-gray-700">Nessun audit eseguito</p>
              <p className="text-sm text-gray-500 mt-1">
                Clicca "Esegui audit" per verificare la coerenza del sistema legale.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading */}
      {loading && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 gap-3">
            <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
            <p className="text-sm text-gray-500">Esecuzione audit in corso…</p>
          </CardContent>
        </Card>
      )}

      {/* Risultati */}
      {data && !loading && (
        <>
          {/* Summary banner */}
          {(() => {
            const cfg = LEVEL_CONFIG[data.summary.status] || LEVEL_CONFIG.OK;
            const Icon = cfg.icon;
            return (
              <div className={`flex items-center gap-4 p-4 rounded-xl border ${cfg.bg} ${cfg.border}`}>
                <Icon className={`w-8 h-8 flex-shrink-0 ${cfg.color}`} />
                <div className="flex-1">
                  <p className={`font-semibold text-lg ${cfg.color}`}>
                    {data.summary.status === 'OK' && 'Audit superato — nessun problema rilevato'}
                    {data.summary.status === 'WARNING' && `Audit con warning — ${data.summary.warnings} warning, 0 errori`}
                    {data.summary.status === 'ERROR' && `Audit fallito — ${data.summary.errors} errori, ${data.summary.warnings} warning`}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Eseguito il {new Date(data.timestamp).toLocaleString('it-IT')}
                    {' · '}{data.summary.total_checks} check eseguiti
                  </p>
                </div>
                <div className="flex gap-2">
                  {data.summary.errors > 0 && (
                    <Badge className="bg-red-100 text-red-800">
                      {data.summary.errors} errori
                    </Badge>
                  )}
                  {data.summary.warnings > 0 && (
                    <Badge className="bg-yellow-100 text-yellow-800">
                      {data.summary.warnings} warning
                    </Badge>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {statsItems.map(({ icon: Icon, label, value }) => (
              <div key={label} className="bg-white border rounded-lg p-3 flex items-center gap-3">
                <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">{label}</p>
                  <p className="text-lg font-bold text-gray-900">{value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Check groups */}
          <div className="space-y-4">
            {Object.entries(grouped).map(([checkNum, group]) => {
              const hasErrors   = group.items.some(i => i.level === 'ERROR');
              const hasWarnings = group.items.some(i => i.level === 'WARNING');
              const groupStatus = hasErrors ? 'ERROR' : hasWarnings ? 'WARNING' : 'OK';

              return (
                <Card key={checkNum}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <span className="text-gray-400 font-mono text-xs">CHECK {checkNum}</span>
                        {group.name}
                      </span>
                      <StatusBadge status={groupStatus} />
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 pt-0">
                    {group.items.map((issue, idx) => (
                      <IssueRow key={idx} issue={issue} />
                    ))}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Info note */}
          <div className="flex items-start gap-2 text-xs text-gray-400 p-3 bg-gray-50 rounded-lg border">
            <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>
              L'audit è in sola lettura e non modifica alcun dato. Per l'esecuzione da terminale:
              {' '}<code className="font-mono bg-gray-200 px-1 rounded">cd backend && python scripts/audit_legal.py</code>
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminLegalAuditPage;
